/**
 * Lineage Tree View Component
 * Shows hierarchical database structure like Unidatum
 */
import React, { useState } from 'react';
import { Box, Typography, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FolderIcon from '@mui/icons-material/Folder';
import TableChartIcon from '@mui/icons-material/TableChart';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import StorageIcon from '@mui/icons-material/Storage';
import AssessmentIcon from '@mui/icons-material/Assessment';

interface LineageTreeViewProps {
  selectedColumn?: string;
}

const TreeNode: React.FC<{
  label: React.ReactNode;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  defaultExpanded?: boolean;
  level?: number;
  isSelected?: boolean;
}> = ({ label, icon, children, defaultExpanded = false, level = 0, isSelected = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Box>
      <Box
        onClick={() => children && setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          py: 0.5,
          px: 1,
          pl: level * 2 + 1,
          cursor: children ? 'pointer' : 'default',
          '&:hover': {
            bgcolor: '#f0f0f0'
          },
          ...(isSelected && {
            bgcolor: '#e3f2fd'
          })
        }}
      >
        {children ? (
          expanded ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />
        ) : (
          <Box sx={{ width: 16 }} />
        )}
        {icon}
        {label}
      </Box>
      {children && (
        <Collapse in={expanded} timeout="auto">
          {children}
        </Collapse>
      )}
    </Box>
  );
};

const LineageTreeView: React.FC<LineageTreeViewProps> = ({ selectedColumn = 'C_CUSTKEY' }) => {
  return (
    <Box sx={{ 
      p: 2,
      height: '100%'
    }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#666' }}>
        Browse
      </Typography>

      {/* Database Root */}
      <TreeNode
        label={<Typography sx={{ fontSize: '13px', fontWeight: 600 }}>DATABASE</Typography>}
        icon={<StorageIcon sx={{ fontSize: 16, color: '#666' }} />}
        defaultExpanded={true}
        level={0}
      >
        {/* Power BI Server */}
        <TreeNode
          label={<Typography sx={{ fontSize: '13px', fontWeight: 600 }}>POWER BI SERVER</Typography>}
          icon={<StorageIcon sx={{ fontSize: 16, color: '#666' }} />}
          defaultExpanded={true}
          level={1}
        >
          {/* Workspace ID */}
          <TreeNode
            label={<Typography sx={{ fontSize: '12px' }}>3f5befef-44a9-4ccb-8c92-603315fcdd7</Typography>}
            icon={<FolderIcon sx={{ fontSize: 16, color: '#999' }} />}
            defaultExpanded={true}
            level={2}
          >
            {/* Trial */}
            <TreeNode
              label={<Typography sx={{ fontSize: '12px' }}>Trial-20240116T155551Z-rNz-aLOw</Typography>}
              icon={<FolderIcon sx={{ fontSize: 16, color: '#999' }} />}
              defaultExpanded={true}
              level={3}
            >
              {/* Demo-UI */}
              <TreeNode
                label={<Typography sx={{ fontSize: '12px', fontWeight: 600 }}>Demo-UI</Typography>}
                icon={<FolderIcon sx={{ fontSize: 16, color: '#999' }} />}
                defaultExpanded={true}
                level={4}
              >
                {/* Datasets */}
                <TreeNode
                  label={<Typography sx={{ fontSize: '12px', fontWeight: 600 }}>Datasets</Typography>}
                  icon={<FolderIcon sx={{ fontSize: 16, color: '#666' }} />}
                  defaultExpanded={true}
                  level={5}
                >
                  {/* Sales Analysis Dataset */}
                  <TreeNode
                    label={<Typography sx={{ fontSize: '12px' }}>Sales Analysis Dataset (Dem</Typography>}
                    icon={<FolderIcon sx={{ fontSize: 16, color: '#999' }} />}
                    defaultExpanded={true}
                    level={6}
                  >
                    {/* CUSTOMER Table */}
                    <TreeNode
                      label={<Typography sx={{ fontSize: '12px', fontWeight: 700 }}>CUSTOMER</Typography>}
                      icon={<TableChartIcon sx={{ fontSize: 16, color: '#f4b942' }} />}
                      defaultExpanded={true}
                      level={7}
                      isSelected={true}
                    >
                      {/* Columns */}
                      <TreeNode
                        label={<Typography sx={{ fontSize: '11px', fontWeight: selectedColumn === 'C_CUSTKEY' ? 700 : 400 }}>C_CUSTKEY</Typography>}
                        icon={<ViewColumnIcon sx={{ fontSize: 14, color: '#f4b942' }} />}
                        level={8}
                        isSelected={selectedColumn === 'C_CUSTKEY'}
                      />
                      <TreeNode
                        label={<Typography sx={{ fontSize: '11px', fontWeight: selectedColumn === 'C_NAME' ? 700 : 400 }}>C_NAME</Typography>}
                        icon={<ViewColumnIcon sx={{ fontSize: 14, color: '#f4b942' }} />}
                        level={8}
                        isSelected={selectedColumn === 'C_NAME'}
                      />
                      <TreeNode
                        label={<Typography sx={{ fontSize: '11px', fontWeight: selectedColumn === 'C_ADDRESS' ? 700 : 400 }}>C_ADDRESS</Typography>}
                        icon={<ViewColumnIcon sx={{ fontSize: 14, color: '#f4b942' }} />}
                        level={8}
                        isSelected={selectedColumn === 'C_ADDRESS'}
                      />
                      <TreeNode
                        label={<Typography sx={{ fontSize: '11px', fontWeight: selectedColumn === 'C_NATIONKEY' ? 700 : 400 }}>C_NATIONKEY</Typography>}
                        icon={<ViewColumnIcon sx={{ fontSize: 14, color: '#f4b942' }} />}
                        level={8}
                        isSelected={selectedColumn === 'C_NATIONKEY'}
                      />
                      <TreeNode
                        label={<Typography sx={{ fontSize: '11px', fontWeight: selectedColumn === 'C_PHONE' ? 700 : 400 }}>C_PHONE</Typography>}
                        icon={<ViewColumnIcon sx={{ fontSize: 14, color: '#f4b942' }} />}
                        level={8}
                        isSelected={selectedColumn === 'C_PHONE'}
                      />
                      <TreeNode
                        label={<Typography sx={{ fontSize: '11px', fontWeight: selectedColumn === 'C_ACCTBAL' ? 700 : 400 }}>C_ACCTBAL</Typography>}
                        icon={<ViewColumnIcon sx={{ fontSize: 14, color: '#f4b942' }} />}
                        level={8}
                        isSelected={selectedColumn === 'C_ACCTBAL'}
                      />
                      <TreeNode
                        label={<Typography sx={{ fontSize: '11px', fontWeight: selectedColumn === 'C_MKTSEGMENT' ? 700 : 400 }}>C_MKTSEGMENT</Typography>}
                        icon={<ViewColumnIcon sx={{ fontSize: 14, color: '#f4b942' }} />}
                        level={8}
                        isSelected={selectedColumn === 'C_MKTSEGMENT'}
                      />
                    </TreeNode>

                    {/* NATION Table */}
                    <TreeNode
                      label={<Typography sx={{ fontSize: '12px', fontWeight: 600 }}>NATION</Typography>}
                      icon={<TableChartIcon sx={{ fontSize: 16, color: '#f4b942' }} />}
                      level={7}
                    />

                    {/* ORDERS Table */}
                    <TreeNode
                      label={<Typography sx={{ fontSize: '12px', fontWeight: 600 }}>ORDERS</Typography>}
                      icon={<TableChartIcon sx={{ fontSize: 16, color: '#f4b942' }} />}
                      level={7}
                    />

                    {/* v_local_supplier_volume Table */}
                    <TreeNode
                      label={<Typography sx={{ fontSize: '12px', fontWeight: 600 }}>v_local_supplier_volume</Typography>}
                      icon={<TableChartIcon sx={{ fontSize: 16, color: '#f4b942' }} />}
                      level={7}
                    />
                  </TreeNode>

                  {/* Reports Folder */}
                  <TreeNode
                    label={<Typography sx={{ fontSize: '12px', fontWeight: 600 }}>Reports</Typography>}
                    icon={<FolderIcon sx={{ fontSize: 16, color: '#666' }} />}
                    level={6}
                  />
                </TreeNode>
              </TreeNode>
            </TreeNode>
          </TreeNode>
        </TreeNode>

        {/* Stats */}
        <TreeNode
          label={<Typography sx={{ fontSize: '13px', fontWeight: 600 }}>Stats</Typography>}
          icon={<AssessmentIcon sx={{ fontSize: 16, color: '#666' }} />}
          level={1}
        />
      </TreeNode>
    </Box>
  );
};

export default LineageTreeView;
